;(async () => {
  if (!customElements.get('size-guide')) {
    const { default: Micromodal } = await import('micromodal')

    class SizeGuide extends HTMLElement {
      constructor() {
        super()

        this.defaultOptions = {}
        this.options = {
          ...this.defaultOptions,
          ...JSON.parse(this.dataset.sizeGuideOptions),
        }

        this.dataSources = {}
        this.options.collections.forEach((collection) => {
          this.dataSources[collection.sizeguide_collection] = collection.sizeguide_datasource
        })

        this.modalWindowId = 'size-guide-modal'
        this.currentCollection = this.options.currentCollection
        this.spreadSheetData = []
        this.localStorageInches = 'size-guide-inches'
        this.storedInches = localStorage.getItem(this.localStorageInches)
        this.showInches = this.options.defaultInches || false
        this.isHelperPopupVisible = false
        this.isHelperPopupVisibleClass = 'size-guide-popup--is-helper-popup-visible'
        this.onHelperCellPopupBodyClick = this.handleHelperPopupBodyClick.bind(this)
        this.isLoadingDataClass = 'size-guide-popup--is-loading'
        this.dataLocalStorageKeyPrefix = 'size-guide-data-v2-'
        this.LocalStorageKeyToken = 'size-guide-data-token'

        // Set the current size guide token
        this.handleCurrentSizeguideToken()

        // Load data on initial call if needed
        this.fetchInitialSizeGuideData()

        // Set initial state and attach event to cm/inch switch
        if (this.storedInches) {
          this.showInches = JSON.parse(this.storedInches)
        }

        this.querySelector('[data-size-guide-show-inches]').checked = this.showInches
        this.querySelector('[data-size-guide-show-inches]').addEventListener('change', this.toggleCmInch.bind(this))

        // Attach event to helper popup close
        this.querySelector('[data-size-guide-popup-helper-close-element]').addEventListener('click', this.closeHelperCellPopup.bind(this))

        this.querySelector('[data-size-guide-close]').addEventListener('click', (event) => {
          event.preventDefault()
          Micromodal.close(this.modalWindowId)
        })
      }

      /**
       * Set the current size guide token to local storage.
       */
      handleCurrentSizeguideToken() {
        // Get the current size guide token
        this.currentSizeguideToken = localStorage.getItem(this.LocalStorageKeyToken)

        // Set the size guide token
        this.options.sizeguideToken.trim().length !== 0 ? (this.newSizeguideToken = this.options.sizeguideToken) : (this.newSizeguideToken = 'default')
        localStorage.setItem(this.LocalStorageKeyToken, this.newSizeguideToken)
      }

      /**
       * Fetch initial size guide data on component load
       */
      async fetchInitialSizeGuideData() {
        for (const dataSourceCollection in this.dataSources) {
          const dataLocalStorageKey = this.dataLocalStorageKeyPrefix + dataSourceCollection
          const dataArr = JSON.parse(localStorage.getItem(dataLocalStorageKey))

          // Check if we already have the size guide data saved in localstorage
          if (!localStorage.getItem(dataLocalStorageKey) || dataArr.length === 0 || this.currentSizeguideToken === null || this.currentSizeguideToken !== this.newSizeguideToken) {
            try {
              // Fetch the spreadsheet data
              this.spreadSheetData[dataSourceCollection] = await this.fetchSpreadsheetData(this.dataSources[dataSourceCollection])

              // Save data in local storage
              if ((this.spreadSheetData?.[dataSourceCollection] || []).length > 0) {
                localStorage.setItem(dataLocalStorageKey, JSON.stringify(this.spreadSheetData[dataSourceCollection]))
              }
            } catch (error) {
              console.error('Error rendering collection data', error)
            }
          }
        }
      }

      /**
       * Set the initial collection (when triggered from a non-collection page)
       */
      setInitialCollection(initialCollection) {
        this.currentCollection = initialCollection
      }

      /**
       * Open size guide modal dialog
       */
      open() {
        const self = this

        Micromodal.show(this.modalWindowId, {
          openClass: 'size-guide-popup--is-open',
          disableScroll: true,
          disableFocus: true,
          awaitOpenAnimation: true,
          awaitCloseAnimation: true,
          onShow: () => {
            self.buildCollectionSelect()
            self.renderSelectedCollection()
          },
          onClose: () => {
            // Reset states
            self.currentCollection = self.options.currentCollection
            self.spreadSheetData = []
            self.closeHelperCellPopup()
          },
        })
      }

      /**
       * Get the current collection
       * @returns {Object}
       */
      getCurrentCollectionData() {
        return this.options.collections?.find((collection) => collection.sizeguide_collection === this.currentCollection) || this.options.collections[0]
      }

      /**
       * Toggle between cm/inch
       * @param {Event} event
       */
      toggleCmInch(event) {
        this.showInches = event.target.checked
        localStorage.setItem(this.localStorageInches, JSON.stringify(this.showInches))
        this.renderSelectedCollection()
      }

      /**
       * Build the collection switcher
       */
      buildCollectionSelect() {
        const selectElem = this.querySelector('[data-size-guide-collections]')
        let selectHtml

        this.options.collections.forEach((collection) => {
          const isSelected = collection.sizeguide_collection === this.currentCollection ? 'selected' : ''
          selectHtml += `<option value='${collection.sizeguide_collection}' ${isSelected}>${collection.collection_name}</option>`
        })

        selectElem.innerHTML = selectHtml
        selectElem.addEventListener('change', this.switchCollection.bind(this))
      }

      /**
       * Switch selected collection
       * @param {Event} event
       */
      switchCollection(event) {
        this.currentCollection = event.target.value
        this.spreadSheetData = []
        this.renderSelectedCollection()
      }

      /**
       * Bind helper cell popup events
       */
      bindHelperCellPopups() {
        this.querySelectorAll('[data-size-guide-popup-helper-trigger]').forEach((elem) => {
          const self = this

          elem.addEventListener('click', function (event) {
            const title = this.dataset.sizeGuidePopupHelperTitle
            const content = this.dataset.sizeGuidePopupHelperContent
            const image = this.dataset.sizeGuidePopupHelperImage

            self.openHelperCellPopup(event, title, content, image)
          })
        })
      }

      /**
       * Populate and show helper cell popup
       * @param {Event} event
       * @param {String} title
       * @param {String} content
       * @param {String} image
       */
      openHelperCellPopup(event, title, content, image) {
        event.preventDefault()
        event.stopImmediatePropagation()

        // Strip HTML from title and build up the content
        const popupTitle = title.replace(/(<([^>]+)>)/gi, '')
        let popupContent = `<p>${content}</p>`

        // Check if we have an image to add to the content
        if (image && image !== null && image !== 'undefined') {
          popupContent += `<img src='${image}' class='size-guide-popup__helper-image' alt='Image' />`
        }

        const popupTitleElement = this.querySelector('[data-size-guide-popup-helper-title-element]')

        if (popupTitle) {
          popupTitleElement.innerHTML = popupTitle
        } else {
          popupTitleElement.hide()
        }

        const popupContentElement = this.querySelector('[data-size-guide-popup-helper-content-element]')

        if (popupContent) {
          popupContentElement.innerHTML = popupContent
        } else {
          popupContentElement.hide()
        }

        document.body.addEventListener('click', this.onHelperCellPopupBodyClick)
        this.isHelperPopupVisible = true
        this.classList.add(this.isHelperPopupVisibleClass)
      }

      /**
       * Close helper cell popup
       */
      closeHelperCellPopup() {
        document.body.removeEventListener('click', this.onHelperCellPopupBodyClick)
        this.isHelperPopupVisible = false
        this.classList.remove(this.isHelperPopupVisibleClass)
      }

      /**
       * On body click when a cell helper popup is open
       * @param {Event} event
       */
      handleHelperPopupBodyClick(event) {
        const target = event.target
        const popupHelperElementSelector = '[data-size-guide-popup-helper-element]'
        const popupHelperElement = this.querySelector(popupHelperElementSelector)

        if (this.isHelperPopupVisible && target !== popupHelperElement && !target.closest(popupHelperElementSelector)) {
          this.closeHelperCellPopup()
        }
      }

      /**
       * Fetch spreadsheet data
       * @param {String} dataSource
       * @param {Number} spreadsheetCounter
       * @returns {Array|undefined}
       */
      async fetchSpreadsheetData(dataSource, spreadsheetCounter) {
        spreadsheetCounter = spreadsheetCounter || 0
        const maxSpreadsheetCounter = 10

        try {
          const response = await fetch(dataSource)

          if (response.status !== 200) {
            throw new Error(response.status)
          }

          const responseData = await response.text()
          const dataRows = responseData.split('\r\n').map((row) => row.split(','))

          return dataRows
        } catch (error) {
          // Error, this sometimes happens with Google, try again a few times till we have it right
          if (spreadsheetCounter < maxSpreadsheetCounter) {
            return this.fetchSpreadsheetData(dataSource, spreadsheetCounter + 1)
          } else {
            console.error('Error fetching spreadsheet.', error)
            return undefined
          }
        }
      }

      /**
       * Render selected collection data.
       */
      async renderSelectedCollection() {
        const collectionData = this.getCurrentCollectionData()
        const dataSource = collectionData?.sizeguide_datasource
        const localStorageKey = this.dataLocalStorageKeyPrefix + collectionData.sizeguide_collection
        let dataRows

        // Check if we already have the size guide data saved in localstorage
        if (localStorage.getItem(localStorageKey)) {
          const dataRowsString = localStorage.getItem(localStorageKey) || []
          dataRows = JSON.parse(dataRowsString)
        } else {
          if (!this.spreadSheetData?.[collectionData.sizeguide_collection]) {
            // Start loader
            this.classList.add(this.isLoadingDataClass)

            try {
              // Fetch the spreadsheet data
              this.spreadSheetData[collectionData.sizeguide_collection] = await this.fetchSpreadsheetData(dataSource)
            } catch (error) {
              console.error('Error rendering selected collection.', { collectionData, error })
            }
          }

          dataRows = this.spreadSheetData?.[collectionData.sizeguide_collection] || []
        }

        // Clear tables and fill them up again
        this.querySelectorAll('[data-remove-on-refresh]').forEach((elem) => elem.remove())

        if (!dataRows.length) {
          return
        }

        // Build up a rows array for easy access
        let dataColumns, measurementColumns, lengthColumns

        switch (collectionData.sizeguide_type) {
          case 'sweaters':
            dataColumns = false
            measurementColumns = this.showInches ? [4, 5, 6] : [1, 2, 3]
            lengthColumns = false
            break
          case 'tops':
            dataColumns = false
            measurementColumns = this.showInches ? [5, 6, 7, 8] : [1, 2, 3, 4]
            lengthColumns = false
            break
          case 'tops5':
            dataColumns = false
            measurementColumns = this.showInches ? [6, 7, 8, 9, 10] : [1, 2, 3, 4, 5]
            lengthColumns = false
            break
          case 'tops6':
            dataColumns = false
            measurementColumns = this.showInches ? [7, 8, 9, 10, 11, 12] : [1, 2, 3, 4, 5, 6]
            lengthColumns = false
            break
          case 'longs':
            dataColumns = [1, 2, 3, 4, 5]
            measurementColumns = this.showInches ? [10, 11, 12, 13] : [6, 7, 8, 9]
            lengthColumns = this.showInches ? [17, 18] : [15, 16]
            break
          case 'juniors':
            dataColumns = [1, 2, 3, 4]
            measurementColumns = this.showInches ? [10, 11, 12, 13, 14] : [5, 6, 7, 8, 9]
            lengthColumns = false
            break
          default:
            // Default to the shorts, this is mostly used
            dataColumns = [1, 2, 3, 4, 5]
            measurementColumns = this.showInches ? [11, 12, 13, 14, 15] : [6, 7, 8, 9, 10]
            lengthColumns = false
        }

        // Render tables, columns and rows
        this.renderDataColumns(dataColumns, collectionData)
        this.renderMeasurementColumns(measurementColumns, collectionData)
        this.renderLengthColumns(lengthColumns, collectionData)
        this.renderDataRows(dataRows, dataColumns, measurementColumns, lengthColumns)

        // Render additional collection (non data) content
        this.renderAdditionalCollectionContent(collectionData)

        // Bind clicks on helper cells
        this.bindHelperCellPopups()

        // Remove loader
        this.classList.remove(this.isLoadingDataClass)
      }

      /**
       * Render data columns
       * @param {Array} dataColumns
       * @param {Object} collectionData
       */
      renderDataColumns(dataColumns, collectionData) {
        const elemTableWrapper = this.querySelector('[data-size-guide-data-table-wrapper]')
        const elemDescription = this.querySelector('[data-size-guide-data-description]')

        if (!dataColumns) {
          elemTableWrapper.classList.add('hidden')
          elemDescription.classList.add('hidden')
          return
        }

        elemTableWrapper.classList.remove('hidden')
        elemDescription.classList.remove('hidden')

        const dataTable = this.querySelector('[data-size-guide-data-table]')
        let helperCells = ''
        let titleCells = ''

        dataColumns.forEach((col, i) => {
          const index = i + 1
          let columnContent = '&nbsp;'

          const checkContent = collectionData[`column_content_${index}`]
          const checkImage = collectionData[`column_image_${index}`]

          if (checkContent != '' || checkImage) {
            columnContent = `<span class='size-guide-popup__helper-trigger center weight-demibold' data-size-guide-popup-helper-trigger data-size-guide-popup-helper-title='${
              collectionData[`table_header_${index}`]
            }' data-size-guide-popup-helper-content='${checkContent}' data-size-guide-popup-helper-image='${checkImage}'>?</span>`
          }

          helperCells += `<div class='table-td'>${columnContent}</div>`
          titleCells += `<div class='table-th weight-demibold'>${collectionData[`table_header_${index}`]}</div>`
        })

        dataTable.insertAdjacentHTML('beforeend', `<div class='table-row helper-cells' data-remove-on-refresh>${helperCells}</div>`)
        dataTable.insertAdjacentHTML('beforeend', `<div class='table-row' data-remove-on-refresh>${titleCells}</div>`)
      }

      /**
       * Render measurement columns
       * @param {Array} measurementColumns
       * @param {Object} collectionData
       */
      renderMeasurementColumns(measurementColumns, collectionData) {
        const measurementsTable = this.querySelector('[data-size-guide-measurements-table]')
        let helperCells = ''
        let titleCells = ''

        measurementColumns.forEach((col, i) => {
          const index = i + 1
          let columnContent = '&nbsp;'

          const checkContent = collectionData[`measurements_content_${index}`]
          const checkImage = collectionData[`measurements_image_${index}`]

          if (checkContent != '' || checkImage) {
            columnContent = `<span class='size-guide-popup__helper-trigger center weight-demibold' data-size-guide-popup-helper-trigger data-size-guide-popup-helper-title='${
              collectionData[`measurements_header_${index}`]
            }' data-size-guide-popup-helper-content='${checkContent}' data-size-guide-popup-helper-image='${checkImage}'>?</span>`
          }

          helperCells += `<div class='table-td'>${columnContent}</div>`
          titleCells += `<div class='table-th weight-demibold'>${collectionData[`measurements_header_${index}`]}</div>`
        })

        measurementsTable.insertAdjacentHTML('beforeend', `<div class='table-row helper-cells' data-remove-on-refresh>${helperCells}</div>`)
        measurementsTable.insertAdjacentHTML('beforeend', `<div class='table-row' data-remove-on-refresh>${titleCells}</div>`)
      }

      /**
       * Render length columns
       * @param {Array} lengthColumns
       * @param {Object} collectionData
       */
      renderLengthColumns(lengthColumns, collectionData) {
        const elemTableWrapper = this.querySelector('[data-size-guide-length-table-wrapper]')
        const elemDescription = this.querySelector('[data-size-guide-length-description]')

        if (!lengthColumns) {
          elemTableWrapper.classList.add('hidden')
          elemDescription.classList.add('hidden')
          return
        }

        elemTableWrapper.classList.remove('hidden')
        elemDescription.classList.remove('hidden')

        const lengthTable = this.querySelector('[data-size-guide-length-table]')
        let helperCells = ''
        let titleCells = ''

        lengthColumns.forEach((col, i) => {
          const index = i + 1
          let columnContent = '&nbsp;'

          const checkContent = collectionData[`length_content_${index}`]
          const checkImage = collectionData[`length_image_${index}`]

          if (checkContent != '' || checkImage) {
            columnContent = `<span class='size-guide-popup__helper-trigger center weight-demibold' data-size-guide-popup-helper-trigger data-size-guide-popup-helper-title='${
              collectionData[`length_header_${index}`]
            }' data-size-guide-popup-helper-content='${checkContent}' data-size-guide-popup-helper-image='${checkImage}'>?</span>`
          }

          helperCells += `<div class='table-td'>${columnContent}</div>`
          titleCells += `<div class='table-th weight-demibold'>${collectionData[`length_header_${index}`]}</div>`
        })

        lengthTable.insertAdjacentHTML('beforeend', `<div class='table-row helper-cells' data-remove-on-refresh>${helperCells}</div>`)
        lengthTable.insertAdjacentHTML('beforeend', `<div class='table-row' data-remove-on-refresh>${titleCells}</div>`)
      }

      /**
       * Render data rows
       * @param {Array} dataRows
       * @param {Array} dataColumns
       * @param {Array} measurementColumns
       * @param {Array} lengthColumns
       */
      renderDataRows(dataRows, dataColumns, measurementColumns, lengthColumns) {
        const dataIndex = this.querySelector('[data-size-guide-index-table]')
        const dataTable = this.querySelector('[data-size-guide-data-table]')
        const measurementsIndex = this.querySelector('[data-size-guide-measurements-index]')
        const measurementsTable = this.querySelector('[data-size-guide-measurements-table]')
        const lengthIndex = this.querySelector('[data-size-guide-length-index]')
        const lengthTable = this.querySelector('[data-size-guide-length-table]')

        // Loop through rows skipping the first row
        dataRows.slice(1).forEach((row, i) => {
          // Fill index tables first
          dataIndex.insertAdjacentHTML('beforeend', `<div class='table-row' data-remove-on-refresh><div class='table-td weight-demibold'>${row[0]}</div></div>`)
          measurementsIndex.insertAdjacentHTML('beforeend', `<div class='table-row' data-remove-on-refresh><div class='table-td weight-demibold'>${row[0]}</div></div>`)

          if (row[14] && row[14] !== '') {
            lengthIndex.insertAdjacentHTML('beforeend', `<div class='table-row' data-remove-on-refresh><div class='table-td weight-demibold'>${row[14]}</div></div>`)
          }

          // Fill the data tables
          if (dataColumns) {
            let dataCells = ''

            dataColumns.forEach((col) => {
              dataCells += `<div class='table-td'>${row[col]}</div>`
            })

            if (dataCells.indexOf('undefined') === -1) {
              dataTable.insertAdjacentHTML('beforeend', `<div class='table-row' data-remove-on-refresh>${dataCells}</div>`)
            }
          }

          // Fill the measurements table
          if (measurementColumns) {
            let measurementCells = ''

            measurementColumns.forEach((col) => {
              measurementCells += `<div class='table-td'>${row[col]}</div>`
            })

            if (measurementCells.indexOf('undefined') === -1) {
              measurementsTable.insertAdjacentHTML('beforeend', `<div class='table-row' data-remove-on-refresh>${measurementCells}</div>`)
            }
          }

          // Fill the length table if applicable
          if (!lengthColumns) return

          let lengthCells = ''
          lengthColumns.forEach((col) => {
            // only add a cell if there's content
            lengthCells += row[col]?.length > 0 ? `<div class='table-td'>${row[col]}</div>` : ''
          })

          if (lengthCells.indexOf('undefined') === -1) {
            lengthTable.insertAdjacentHTML('beforeend', `<div class='table-row' data-remove-on-refresh>${lengthCells}</div>`)
          }
        })
      }

      /**
       * Render additional collection (non data) content
       * @param {Object} collectionData
       */
      renderAdditionalCollectionContent(collectionData) {
        const elemFitSizeHeader = this.querySelector('[data-size-guide-product-fit-size-header]')
        const elemFitSizeContent = this.querySelector('[data-size-guide-product-fit-size]')
        const elemTableName = this.querySelectorAll('[data-size-guide-collection-table-name]')
        const elemCollectionImage = this.querySelector('[data-size-guide-product-image]')

        if (collectionData.sizeFitContent) {
          elemFitSizeContent.innerHTML = unescape(collectionData.sizeFitContent)
          elemFitSizeContent.classList.remove('hidden')
          elemFitSizeHeader.classList.remove('hidden')
        } else {
          elemFitSizeContent.classList.add('hidden')
          elemFitSizeHeader.classList.add('hidden')
        }

        if (collectionData.collection_table_name) {
          elemTableName.forEach((elem) => (elem.innerHTML = collectionData.collection_table_name))
        }

        if (collectionData.collectionImage) {
          elemCollectionImage.setAttribute('src', collectionData.collectionImage)
        }
      }
    }

    window.SizeGuide = SizeGuide
    customElements.define('size-guide', SizeGuide)
  }
})()
